import React, { useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import { withStyles } from 'tss-react/mui';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useDrag, useDrop } from 'react-dnd';

import { accountsActions } from 'companion-app-components/flux/accounts';
import { isEntityShareUser } from 'companion-app-components/flux/account-groups/accountGroupsSelectors';

import ErrorBoundary from 'components/ErrorBoundary';
import ZeroStateView from 'components/ZeroStateView';
import paperAirplaneCrash from 'assets/zero-state-images/paperAirplaneCrash.svg';

// import { getLogger } from 'utils/logger';
// const log = getLogger('containers/OverviewPage/OverviewCard/index.js');

const styles = (theme) => {
  const cardHeaderQuicken = {
    display: 'flex',
    height: 45,
    padding: '0 20px',
    width: '100%',
    margin: 0,
    borderBottom: '1px solid #e0e0e0',
    textAlign: 'left',
    alignItems: 'center',
    backgroundColor: theme.palette.greyScaleDeprecated[7],
    cursor: 'pointer',
    '&:hover :active': {
      cursor: 'grabbing !important',
    },
  };
  return ({
    card: {
      backgroundColor: 'unset',
      textAlign: 'center',
      color: theme.palette.text.secondary,
      border: 'none',
      boxShadow: '',
      borderRadius: 8,
      height: 275,
      overflow: 'hidden',
      '&.noHeader': {
        height: 275,
      },
      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      '&:hover': {
        boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 16px',
        cursor: 'pointer',
        transform: 'translate(0px, -1px)',
      },
    },
    cardDragged: {
      // backgroundColor: '',
      // border: '',
      borderRadius: 8,
      height: 275,
      overflow: 'hidden',
      //  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      '&.noHeader': {
        height: 344,
      },
      //  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    },
    cardHeader: {
      ...theme.typography.body2,
      display: 'flex',
      height: 40,
      margin: '14px 24px 24px',
      padding: 0,
      width: '100%',
      textAlign: 'left',
      alignItems: 'flex-start',
      //  Card header background
      backgroundColor: theme.palette.greyScaleDeprecated[7],
      cursor: 'pointer',
      '&:hover :active': {
        cursor: 'grabbing !important',
      },
    },
    cardHeaderQuicken,
    cardHeaderNotClickable: {
      ...cardHeaderQuicken,
      cursor: 'default',
    },
    actions: {
      alignSelf: 'center',
      marginTop: 0,
    },
    cardContentRoot: {
      padding: '0 !important',
      //  Card background
      backgroundColor: theme.palette.greyScaleDeprecated[7],
      height: 230,
    },
    skeletonWrapper: {
      height: '100%',
      width: '100%',
    },
    skeletonBlock: {
      backgroundColor: theme.palette.greyScaleDeprecated[5],
      borderRadius: 8,
      marginBottom: 10,
      marginLeft: '24px',
      width: '75%',
      height: 30,
      '&.large': {
        marginLeft: '24px',
        width: '85%',
        height: 40,
      },
      '&.small': {
        marginLeft: '24px',
        width: '70%',
        height: 20,
      },
      '&.netWorth': {
        marginLeft: '24px',
        width: '85%',
        height: 60,
      },
    },
    skeletonGroupWrapper: {
      marginBottom: '24px',
    },
    skeletonHeader: {
      width: '100%',
      height: 60,
      display: 'flex',
      flexDirection: 'column',
      outline: 'none',
      padding: '14px 24px',
      backgroundColor: theme.palette.greyScaleDeprecated[5],
      marginBottom: '24px',
    },
  });
};

const OverviewCard = (props) => {
  const { title, actions, classes, id, titleNav, isHeaderClickable, noHeader, subheader, style, reorderCards, cardId, accountDrawer } = props;
  const cardHeader = classes.cardHeaderQuicken;
  // eslint-disable-next-line no-unused-vars
  const [view] = useState(null);
  const dispatch = useDispatch();
  const isEntityShareEnableUser = useSelector(isEntityShareUser);

  const ref = useRef();

  const [, drop] = useDrop({
    accept: 'overview-card',
    hover(item) {
      reorderCards(item.cardId, cardId, false);
    },
    drop(item) {
      reorderCards(item.cardId, cardId, true);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: {
      type: accountDrawer ? 'account-drawer' : 'overview-card',
      cardId,
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const clickCard = useCallback((e) => {
    e.stopPropagation();
    if (titleNav) titleNav(e);
  }, [titleNav]);

  const renderDrawerSkeleton = () => {
    const skeletonDrawer = [];
    for (let i = 0; i < 5; i += 1) {
      skeletonDrawer.push(
        <div key={`skeleton-${i}`} className={classes.skeletonGroupWrapper}>
          <Skeleton variant="rectangular" className={classNames(classes.skeletonBlock, 'large')} />
          <Skeleton variant="rectangular" className={classes.skeletonBlock} />
          <Skeleton variant="rectangular" className={classNames(classes.skeletonBlock, 'small')} />
        </div>,
      );
    }

    return (
      <>
        <Skeleton variant="rectangular" className={classes.skeletonHeader} />
        <div className={classes.skeletonWrapper}>
          <Skeleton variant="rectangular" className={classNames(classes.skeletonBlock, 'netWorth')} />
          {skeletonDrawer}
        </div>
      </>
    );
  };

  if (view !== 'loading') {
    drag(drop(ref));
  }

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {(!isDragging) ?
        <Card
          key={`full-${id}`}
          className={classNames(classes.card, noHeader ? 'noHeader' : '')}
          id={`full-${id}`}
          elevation={1}
          onClick={clickCard}
          ref={ref}
          style={isDragging ? { opacity: 0.5 } : {}}
        >
          {!noHeader &&
          (
            <CardHeader
              id={id}
              title={title}
              subheader={subheader}
              titleTypographyProps={{
                id: `${id}-title`,
                variant: 'subtitle1',
                color: 'textPrimary',
              }}
              subheaderTypographyProps={{
                variant: 'body2',
                color: 'textSecondary',
              }}
              classes={{
                root: isHeaderClickable ? cardHeader : classes.cardHeaderNotClickable,
                action: classes.actions,
                title: classes.title,
              }}
              action={actions}
            />
          )}
          {(view === 'loading') && (
            renderDrawerSkeleton()
          )}
          {(view !== 'loading') && (
            <CardContent
              classes={{ root: classes.cardContentRoot }}
              style={style}
            >
              {view === 'try-again' ? (
                <ZeroStateView
                  icon={paperAirplaneCrash}
                  primary="Oops, we ran into an error gathering your accounts."
                  button="try again"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(accountsActions.getAccounts());
                  }}
                />
              ) : (
                <ErrorBoundary>
                  {props.children}
                </ErrorBoundary>
              )}
            </CardContent>
          )}
        </Card>
        :
        <Card
          className={classNames(classes.cardDragged, 'noHeader')}
          id={`full-${id}`}
          elevation={1}
          ref={ref}
        >
          {isEntityShareEnableUser
            ? <Typography color="error" p={2}>Cards re-ordering not allowed for entity shared user.</Typography>
            : <CardContent classes={{ root: classes.cardContentRoot }} style={style} />}
        </Card>}
    </>
  );
};

OverviewCard.propTypes = {
  actions: PropTypes.object,
  children: PropTypes.node,
  isHeaderClickable: PropTypes.bool,
  noHeader: PropTypes.bool,
  classes: PropTypes.object,
  title: PropTypes.string,
  subheader: PropTypes.string,
  id: PropTypes.string,
  titleNav: PropTypes.func,
  style: PropTypes.object,
  reorderCards: PropTypes.func,
  cardId: PropTypes.object,
  accountDrawer: PropTypes.bool,
};

export default withStyles(OverviewCard, styles);

